import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ShieldCheck,
  Loader2,
  AlertTriangle,
  ArrowLeft,
  Lightbulb,
  Hash,
  Clock,
  Download,
  UserPlus,
} from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

function scoreVerdict(score) {
  if (score >= 80) return { label: "Strong", tone: "emerald" };
  if (score >= 65) return { label: "Promising", tone: "lime" };
  if (score >= 50) return { label: "Needs work", tone: "amber" };
  return { label: "Rewrite recommended", tone: "red" };
}

const TONE_CLASSES = {
  emerald: "text-emerald-300 border-emerald-500/30 bg-emerald-500/5",
  lime: "text-lime-300 border-lime-500/30 bg-lime-500/5",
  amber: "text-amber-300 border-amber-500/30 bg-amber-500/5",
  red: "text-red-300 border-red-500/30 bg-red-500/5",
};

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AuditResult() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [generating, setGenerating] = useState(false);

  // Wraps the printable dossier — score card + sequence + improvements grid.
  // Header/back-link/CTA bar live OUTSIDE this ref so the PDF stays clean.
  const dossierRef = useRef(null);
  // Guard so the auto-trigger only ever fires once per page load.
  const autoFiredRef = useRef(false);

  // Load the audit report from the API.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/audit/report/${id}`);
        if (!cancelled) setReport(r.data);
      } catch (err) {
        if (!cancelled) {
          const detail = err?.response?.data?.detail;
          setError(typeof detail === "string" ? detail : "Could not load this audit.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const verdict = useMemo(
    () => scoreVerdict(report?.score ?? 0),
    [report?.score],
  );

  // -------------------------------------------------------------------------
  // PDF synthesis: html2canvas → jspdf, downloaded as OutreachOS_Audit.pdf.
  // We render the dossier section at 2x scale for crisper output, then split
  // it across A4 pages if it overflows. Background is forced to #0a0a0a so
  // dark-theme styling survives the snapshot.
  // -------------------------------------------------------------------------
  const generatePDF = useCallback(async () => {
    const node = dossierRef.current;
    if (!node) {
      toast.error("Nothing to export yet.");
      return;
    }
    setGenerating(true);
    try {
      const canvas = await html2canvas(node, {
        backgroundColor: "#0a0a0a",
        scale: 2,
        useCORS: true,
        logging: false,
        windowWidth: node.scrollWidth,
      });
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF({ unit: "pt", format: "a4", compress: true });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Scale the rendered canvas to fit the page width, keeping aspect ratio.
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight; // negative offset = scroll up
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save("OutreachOS_Audit.pdf");
      toast.success("PDF downloaded.");
    } catch (err) {
      toast.error("PDF generation failed. Try again.");
      // eslint-disable-next-line no-console
      console.error("PDF error:", err);
    } finally {
      setGenerating(false);
    }
  }, []);

  // -------------------------------------------------------------------------
  // Funnel gate: anonymous → /register; stash claim id in localStorage so the
  // freshly-created account adopts THIS audit on the very next request.
  // -------------------------------------------------------------------------
  const handleAnonClick = useCallback(() => {
    try {
      window.localStorage.setItem("pending_audit_claim", id);
    } catch {
      // localStorage can throw in private mode / disabled cookies — proceed
      // anyway, the worst case is the user has to navigate back manually.
    }
    navigate("/register");
  }, [id, navigate]);

  // -------------------------------------------------------------------------
  // Auto-trigger: if the URL carries ?pdf=1 AND the user is now authenticated
  // AND the report has loaded, fire the download exactly once. This is the
  // landing-after-register hop closing the loop.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (autoFiredRef.current) return;
    if (authLoading) return;
    if (!user) return;
    if (!report) return;
    if (params.get("pdf") !== "1") return;
    autoFiredRef.current = true;
    // Slight delay so layout + fonts are settled before html2canvas runs.
    const t = setTimeout(() => {
      generatePDF();
    }, 350);
    return () => clearTimeout(t);
  }, [authLoading, user, report, params, generatePDF]);

  if (loading) {
    return (
      <div
        data-testid="audit-result-loading"
        className="min-h-[40vh] flex items-center justify-center text-neutral-500 text-sm font-mono"
      >
        <Loader2 className="w-4 h-4 animate-spin mr-2" strokeWidth={1.8} />
        Loading audit…
      </div>
    );
  }

  if (error || !report) {
    return (
      <div
        data-testid="audit-result-error"
        className="max-w-2xl rounded-md border border-amber-400/20 bg-amber-400/5 p-5 text-sm text-amber-200 flex gap-3"
      >
        <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" strokeWidth={1.8} />
        <div className="flex-1">
          <div className="font-medium">Audit unavailable</div>
          <div className="mt-1 text-amber-200/80">
            {error || "We couldn't find that audit report."}
          </div>
          <Link
            to="/audit"
            className="inline-flex items-center gap-1.5 mt-4 text-xs font-mono uppercase tracking-[0.2em] text-amber-100 hover:underline"
          >
            <ArrowLeft className="w-3 h-3" strokeWidth={1.8} />
            Back to auditor
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="audit-result-page" className="max-w-4xl">
      <Link
        to="/audit"
        data-testid="audit-result-back"
        className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.25em] text-neutral-500 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-3 h-3" strokeWidth={1.8} />
        Back to auditor
      </Link>

      {/* ----- CTA bar: auth-gated. Excluded from the PDF on purpose. ----- */}
      <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-neutral-500 font-mono">
          <ShieldCheck className="w-3.5 h-3.5" strokeWidth={1.8} />
          Audit dossier
        </div>
        {user ? (
          <button
            type="button"
            onClick={generatePDF}
            disabled={generating}
            data-testid="audit-download-pdf"
            className="inline-flex items-center gap-2 bg-white text-black hover:bg-neutral-200 transition-colors px-4 py-2 rounded-md font-medium text-sm disabled:opacity-50"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.8} />
            ) : (
              <Download className="w-4 h-4" strokeWidth={1.8} />
            )}
            {generating ? "Generating…" : "Download Report as PDF"}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleAnonClick}
            data-testid="audit-claim-pdf"
            className="inline-flex items-center gap-2 bg-white text-black hover:bg-neutral-200 transition-colors px-4 py-2 rounded-md font-medium text-sm"
          >
            <UserPlus className="w-4 h-4" strokeWidth={1.8} />
            Create a free account to download PDF
          </button>
        )}
      </div>

      {/* ----- Printable dossier: every node inside is captured by jsPDF. ----- */}
      <div ref={dossierRef} data-testid="audit-dossier" className="bg-[#0a0a0a] pt-2">
        <div className="mt-2 grid md:grid-cols-3 gap-5 items-stretch">
          {/* Big Score */}
          <div
            data-testid="audit-result-score-card"
            className="md:col-span-1 border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-6 flex flex-col"
          >
            <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-neutral-500">
              Overall score
            </div>
            <div className="mt-3 flex items-baseline gap-1.5">
              <span
                data-testid="audit-result-score"
                className="font-display text-7xl font-semibold tracking-tight leading-none"
              >
                {report.score}
              </span>
              <span className="text-neutral-600 text-2xl font-mono">/100</span>
            </div>
            <div
              className={`mt-5 inline-flex self-start items-center px-2.5 py-1 rounded-md text-[11px] font-mono uppercase tracking-[0.2em] border ${TONE_CLASSES[verdict.tone]}`}
            >
              {verdict.label}
            </div>
            <div className="mt-auto pt-6 border-t border-[#1a1a1a] grid grid-cols-2 gap-4 text-xs">
              <div className="flex items-center gap-2 text-neutral-500">
                <Hash className="w-3 h-3" strokeWidth={1.8} />
                <span className="font-mono truncate" title={report.audit_id}>
                  {report.audit_id}
                </span>
              </div>
              <div className="flex items-center gap-2 text-neutral-500">
                <Clock className="w-3 h-3" strokeWidth={1.8} />
                <span className="font-mono truncate">
                  {formatDate(report.created_at)}
                </span>
              </div>
            </div>
          </div>

          {/* Original sequence */}
          <div
            data-testid="audit-result-sequence-card"
            className="md:col-span-2 border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-6 flex flex-col"
          >
            <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-neutral-500">
              Original sequence
            </div>
            <pre className="mt-3 flex-1 max-h-72 overflow-auto text-[12.5px] leading-relaxed font-mono text-neutral-300 whitespace-pre-wrap break-words">
              {report.original_sequence}
            </pre>
          </div>
        </div>

        {/* Improvements */}
        <div className="mt-8">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-neutral-500 font-mono">
            <Lightbulb className="w-3.5 h-3.5" strokeWidth={1.8} />
            Highest-leverage rewrites
          </div>
          <div
            data-testid="audit-result-improvements"
            className="mt-4 grid md:grid-cols-3 gap-4"
          >
            {(report.improvements || []).map((tip, i) => (
              <div
                key={`${i}-${tip.slice(0, 32)}`}
                data-testid={`audit-improvement-${i + 1}`}
                className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg p-5 flex flex-col"
              >
                <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-[0.25em] text-neutral-500">
                  <span>Rewrite {String(i + 1).padStart(2, "0")}</span>
                  <span className="text-neutral-700">/03</span>
                </div>
                <p className="mt-3 text-sm text-neutral-200 leading-relaxed">
                  {tip}
                </p>
              </div>
            ))}
            {(!report.improvements || report.improvements.length === 0) && (
              <div className="md:col-span-3 text-sm text-neutral-500 font-mono">
                No improvements were generated for this report.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
